import React, { useMemo } from "react";
import {
  BulkChangeBotsStatusJobArgs,
  JobToastType,
  selectJobsData,
} from "@shared/slices/jobs.slice";
import { useAppSelector } from "app/hooks";
import { useLocalJobData } from "utils/hooks/useLocalJobData";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { BulkCancelProgressData, LanguageNameSpaces } from "@shared/types";
import BulkProgressJobToast from "common/BulkProgressJobToast";
import BulkProgressStats from "common/ProgressToasts/BulkProgressStats.tsx";
import Text from "common/Typography/Text";

interface BulkBotsProgressToastProps {
  notificationApiRef: React.RefObject<any>;
}

const BulkBotsProgressToast: React.FC<BulkBotsProgressToastProps> = ({
  notificationApiRef,
}) => {
  const { localJobs, removeJob, isJobActiveInRedux } = useLocalJobData(
    JobToastType.Bot
  );
  return (
    <>
      {localJobs.map(({ args, id }) => (
        <BotsJobToast
          key={id}
          notificationApiRef={notificationApiRef}
          args={args}
          toastKey={`bulk-deactivate-bots-progress-toast-${id}`}
          onRemove={() => removeJob(id)}
          isJobActiveInRedux={() => isJobActiveInRedux(id)}
        />
      ))}
    </>
  );
};

export default React.memo(BulkBotsProgressToast);

interface BotsJobToastProps {
  notificationApiRef: React.RefObject<any>;
  args: BulkChangeBotsStatusJobArgs;
  toastKey: string;
  onRemove: () => void;
  isJobActiveInRedux: () => boolean;
}

const BotsJobToast: React.FC<BotsJobToastProps> = ({
  notificationApiRef,
  args,
  toastKey,
  onRemove,
  isJobActiveInRedux,
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const selectJobData = useMemo(
    () => selectJobsData("bulkChangeBotsStatusJob", args),
    [args]
  );
  const jobResponse = useAppSelector(selectJobData);
  const rawData = jobResponse?.data?.data ?? jobResponse?.data ?? null;

  const botsData: BulkCancelProgressData | null = rawData?.bots_data ?? null;
  const tradesData: BulkCancelProgressData | null =
    rawData?.trades_data ?? null;
  const hasTradesData = rawData?.trades_data != null;

  const isError = jobResponse?.isError ?? false;
  const errorMessage = jobResponse?.errorMessage;

  const total = args.bot_ids?.length ?? 0;
  const success = botsData?.done ?? 0;
  const failed = botsData?.failed ?? 0;
  const inactive = botsData?.inactive_market ?? 0;
  const completed = success + failed + inactive;

  const titleComplete = t("bulk_deactivate_progress.deactivated_bots");
  const titleInProgress = t("bulk_deactivate_progress.deactivating_bots", {
    total,
  });
  const subtitle = t("bulk_deactivate_progress.processed", {
    completed,
    total,
  });

  const tradesTotal = tradesData
    ? (tradesData.done ?? 0) +
      (tradesData.failed ?? 0) +
      (tradesData.inactive_market ?? 0) +
      (tradesData.pending ?? 0)
    : 0;
  const tradesCompleted = tradesData
    ? (tradesData.done ?? 0) +
      (tradesData.failed ?? 0) +
      (tradesData.inactive_market ?? 0)
    : 0;
  const tradesSubtitle = t("bulk_deactivate_progress.processed", {
    completed: tradesCompleted,
    total: tradesTotal,
  });

  return (
    <BulkProgressJobToast
      notificationApiRef={notificationApiRef}
      toastKey={toastKey}
      total={total}
      jobData={botsData}
      isError={isError}
      titleComplete={titleComplete}
      titleInProgress={titleInProgress}
      titleError={t("bulk_deactivate_progress.error")}
      errorMessage={errorMessage}
      subtitle={subtitle}
      successLabel={t("bulk_deactivate_progress.success")}
      failedLabel={t("bulk_deactivate_progress.failed")}
      onRemove={onRemove}
      isJobActiveInRedux={isJobActiveInRedux}
      tradesData={hasTradesData ? tradesData : undefined}
      tradesTotal={tradesTotal}
      isTradesError={hasTradesData ? (tradesData?.failed ?? 0) > 0 : false}
      extraContent={
        hasTradesData ? (
          <AssociatedTradesSection
            tradesData={tradesData}
            tradesTotal={tradesTotal}
            tradesSubtitle={tradesSubtitle}
            associatedTradesLabel={t(
              "bulk_deactivate_progress.associated_trades_closed"
            )}
            noOpenTradesLabel={t("bulk_deactivate_progress.no_open_trades")}
            successLabel={t("bulk_deactivate_progress.success")}
            failedLabel={t("bulk_deactivate_progress.failed")}
            inactiveLabel={t("bulk_deactivate_progress.inactive_symbols")}
            alreadyClosedLabel={t("bulk_deactivate_progress.already_closed")}
          />
        ) : null
      }
    />
  );
};

interface AssociatedTradesSectionProps {
  tradesData: BulkCancelProgressData | null;
  tradesTotal: number;
  tradesSubtitle: string;
  associatedTradesLabel: string;
  noOpenTradesLabel: string;
  successLabel: string;
  failedLabel: string;
  inactiveLabel: string;
  alreadyClosedLabel?: string;
}

const AssociatedTradesSection: React.FC<AssociatedTradesSectionProps> = ({
  tradesData,
  tradesTotal,
  tradesSubtitle,
  associatedTradesLabel,
  noOpenTradesLabel,
  successLabel,
  failedLabel,
  inactiveLabel,
  alreadyClosedLabel,
}) => {
  const isEmpty = tradesTotal === 0;

  return (
    <div
      style={{
        marginTop: 12,
        paddingTop: 12,
        borderTop: "1px solid rgba(255,255,255,0.1)",
      }}
    >
      <div style={{ fontWeight: 600 }}>{associatedTradesLabel}</div>
      {isEmpty ? (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {noOpenTradesLabel}
        </Text>
      ) : (
        <BulkProgressStats
          jobData={tradesData}
          total={tradesTotal}
          subtitle={tradesSubtitle}
          successLabel={successLabel}
          failedLabel={failedLabel}
          inactiveLabel={inactiveLabel}
          alreadyClosedLabel={alreadyClosedLabel}
        />
      )}
    </div>
  );
};
